/**
 * useMessages — hooks for the direct messaging / announcements feature.
 *
 * Messages flow:
 *  1. Sender composes a message (subject, body, recipient scope)
 *  2. useSendMessage inserts into `messages` table + fans out
 *     in-app notifications via the notifications table
 *  3. useOrgMessages fetches all messages for the user's org (sent + received)
 *  4. useMarkMessageRead marks a message read in `message_reads`
 *  5. useUnreadMessageCount returns count of unread in-app messages
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import type { Message, MessageRecipientScope } from "@/types/database";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SendMessagePayload {
  orgId: string;
  subject: string;
  body: string;
  recipientScope: MessageRecipientScope;
  recipientIds?: string[];         // for 'specific_user' scope
  guestBookingIds?: string[];      // for 'all_guests' scope
  sendAsEmail?: boolean;
}

// ─── Fetch all messages for the user's org ───────────────────────────────────

export function useOrgMessages(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ["messages", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from("messages")
        .select(`
          *,
          sender:profiles!sender_id (
            id, full_name, email, role
          )
        `)
        .eq("organization_id", orgId)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as Message[];
    },
    enabled: !!orgId,
  });
}

// ─── Fetch messages sent by the current user ─────────────────────────────────

export function useSentMessages(orgId: string | null | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["messages-sent", orgId, user?.id],
    queryFn: async () => {
      if (!orgId || !user) return [];
      const { data, error } = await supabase
        .from("messages")
        .select(`
          *,
          sender:profiles!sender_id (
            id, full_name, email, role
          )
        `)
        .eq("organization_id", orgId)
        .eq("sender_id", user.id)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as Message[];
    },
    enabled: !!orgId && !!user,
  });
}

// ─── Fetch messages received by the current user (not sent by them) ──────────

export function useInboxMessages(orgId: string | null | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["messages-inbox", orgId, user?.id],
    queryFn: async () => {
      if (!orgId || !user) return [];
      const { data, error } = await supabase
        .from("messages")
        .select(`
          *,
          sender:profiles!sender_id (
            id, full_name, email, role
          )
        `)
        .eq("organization_id", orgId)
        .neq("sender_id", user.id)
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as Message[];
    },
    enabled: !!orgId && !!user,
  });
}

// ─── Mark a message as read ───────────────────────────────────────────────────

export function useMarkMessageRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (messageId: string) => {
      if (!user) return;
      // upsert — ignore conflict if already read
      const { error } = await supabase
        .from("message_reads")
        .upsert({ message_id: messageId, user_id: user.id }, { onConflict: "message_id,user_id" });
      if (error && !error.message.includes("duplicate")) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages-inbox"] });
      queryClient.invalidateQueries({ queryKey: ["messages-unread-count"] });
    },
  });
}

// ─── Get unread message count (for sidebar badge) ────────────────────────────

export function useUnreadMessageCount(orgId: string | null | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["messages-unread-count", orgId, user?.id],
    queryFn: async () => {
      if (!orgId || !user) return 0;

      // Get all messages in org not sent by this user
      const { data: inboxMessages, error: msgError } = await supabase
        .from("messages")
        .select("id")
        .eq("organization_id", orgId)
        .neq("sender_id", user.id);
      if (msgError) return 0;

      if (!inboxMessages || inboxMessages.length === 0) return 0;
      const allIds = inboxMessages.map((m) => m.id);

      // Get the ones already read
      const { data: reads, error: readError } = await supabase
        .from("message_reads")
        .select("message_id")
        .eq("user_id", user.id)
        .in("message_id", allIds);
      if (readError) return 0;

      const readIds = new Set((reads ?? []).map((r) => r.message_id));
      return allIds.filter((id) => !readIds.has(id)).length;
    },
    enabled: !!orgId && !!user,
    refetchInterval: 30_000, // poll every 30s
  });
}

// ─── Send a message ───────────────────────────────────────────────────────────

export function useSendMessage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (payload: SendMessagePayload) => {
      if (!user) throw new Error("Not authenticated");

      // 1. Insert message record
      const { data: message, error: msgError } = await supabase
        .from("messages")
        .insert({
          organization_id: payload.orgId,
          sender_id: user.id,
          sender_role: user.role,
          subject: payload.subject,
          body: payload.body,
          recipient_scope: payload.recipientScope,
          recipient_ids: payload.recipientIds ?? [],
          guest_booking_ids: payload.guestBookingIds ?? [],
          sent_as_notification: true,
          sent_as_email: payload.sendAsEmail ?? false,
        })
        .select()
        .single();

      if (msgError) throw msgError;

      // 2. Resolve recipient user IDs for in-app notifications
      let recipientUserIds: string[] = [];

      if (payload.recipientScope === "specific_user") {
        recipientUserIds = payload.recipientIds ?? [];
      } else {
        // Fetch org member user IDs based on scope
        const roleFilter: string[] = [];
        if (payload.recipientScope === "all_hosts") roleFilter.push("host");
        if (payload.recipientScope === "all") roleFilter.push("host", "strata_admin", "property_manager");
        // 'all_owners' and 'all_guests' are email-only (no platform accounts)

        if (roleFilter.length > 0) {
          const { data: assignments } = await supabase
            .from("user_assignments")
            .select("user_id")
            .eq("org_id", payload.orgId)
            .in("role", roleFilter);
          recipientUserIds = (assignments ?? []).map((a) => a.user_id);
        }
      }

      // Remove sender from recipients
      recipientUserIds = recipientUserIds.filter((id) => id !== user.id);

      // 3. Fan out in-app notifications
      if (recipientUserIds.length > 0) {
        const senderLabel = user.role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        const notifications = recipientUserIds.map((userId) => ({
          user_id: userId,
          organization_id: payload.orgId,
          type: "message" as const,
          title: `Message from ${senderLabel}: ${payload.subject}`,
          message: payload.body.length > 160 ? payload.body.slice(0, 157) + "…" : payload.body,
          read: false,
          data: { link: "/messages", message_id: message.id },
        }));

        await supabase.from("notifications").insert(notifications);
      }

      return message;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["messages", variables.orgId] });
      queryClient.invalidateQueries({ queryKey: ["messages-sent", variables.orgId] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast({
        title: "Message sent",
        description: "Your message has been delivered to the recipients.",
      });
    },

    onError: (error: Error) => {
      toast({
        title: "Failed to send message",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
